import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import CustomPopUp from "../global/CustomPopup";
import Button from "../organism/Button";
import CustomText from "../atomic/CustomText";
import { US_DIAL_CODE, DEFAULT_COUNTRY_CODE } from "../../constants/countryCodes";
import {
  useSendPhoneVerificationCode,
  useVerifyPhoneCode,
} from "../../services/utils/custom hooks/PhoneVerification/usePhoneVerification";
import { useUpdateProfileMutation } from "../../services/utils/custom hooks/useUpdateProfile";
import useProfile from "../../services/utils/custom hooks/useProfile";
import {
  formatUsNationalNumber,
  isValidUsNationalNumber,
  parsePhoneNumber,
  toE164,
} from "../../utils/helpers/phoneNumberHelpers";
import toastMessages from "../../services/utils/helpers/toastMessage";

interface PhoneVerificationPopupProps {
  initialPhone?: string | null;
  onClose: () => void;
  onVerified?: (e164Phone: string) => void;
}

function PhoneVerificationPopup({
  initialPhone,
  onClose,
  onVerified,
}: Readonly<PhoneVerificationPopupProps>): JSX.Element {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const sendCodeMutation = useSendPhoneVerificationCode();
  const verifyCodeMutation = useVerifyPhoneCode();
  const { mutation: updateProfileMutation, updating } =
    useUpdateProfileMutation();

  const [nationalNumber, setNationalNumber] = useState(() => {
    return formatUsNationalNumber(
      parsePhoneNumber(initialPhone).nationalNumber
    );
  });
  const [otp, setOtp] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);

  const resolvedPhone =
    initialPhone ||
    profile?.user?.mobilePhone ||
    profile?.user?.primaryPhone ||
    "";

  useEffect(() => {
    setNationalNumber(
      formatUsNationalNumber(parsePhoneNumber(resolvedPhone).nationalNumber)
    );
    setOtp("");
    setOtpRequested(false);
  }, [resolvedPhone]);

  const isBusy =
    sendCodeMutation.isPending ||
    verifyCodeMutation.isPending ||
    updating;

  function handlePhoneChange(value: string) {
    setNationalNumber(formatUsNationalNumber(value));
    setOtp("");
    setOtpRequested(false);
  }

  function handleReceiveOtp() {
    const mobilePhone = toE164(DEFAULT_COUNTRY_CODE, nationalNumber);
    if (!mobilePhone || !isValidUsNationalNumber(nationalNumber)) {
      toastMessages.error(
        "Enter a valid 10-digit US phone number before requesting a code."
      );
      return;
    }

    sendCodeMutation.mutate(mobilePhone, {
      onSuccess: () => {
        setOtpRequested(true);
      },
    });
  }

  async function handleVerifyOtp() {
    if (!otp || otp.length !== 6) {
      toastMessages.error("Enter the 6-digit verification code.");
      return;
    }

    const mobilePhone = toE164(DEFAULT_COUNTRY_CODE, nationalNumber);
    if (!mobilePhone) {
      toastMessages.error("Enter a valid phone number.");
      return;
    }

    try {
      await verifyCodeMutation.mutateAsync(otp);

      const user = profile?.user;
      await updateProfileMutation.mutateAsync({
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
        email: user?.email ?? "",
        profileImage: user?.imageUrl || user?.profileImage || "",
        mobilePhone,
        ...(user?.roles?.includes("Physician")
          ? {
              npi: user.npi || "",
              suffix: user.suffix || "",
              specialty: user.specialty || "",
            }
          : {}),
      });

      await queryClient.invalidateQueries({
        queryKey: ["phoneVerificationStatus"],
      });
      await queryClient.invalidateQueries({ queryKey: ["profile"] });

      onVerified?.(mobilePhone);
      onClose();
    } catch {
      // Toasts handled in mutations
    }
  }

  return (
    <CustomPopUp
      popUpTitle="Verify phone number"
      popUpSubtitle="Enter your US mobile number, then verify with a one-time code."
      hidePopUp={onClose}
      warningPopupNotificationSettings
      subTitleClassName="!my-1 text-textSecondaryGray"
    >
      <div className="space-y-4 pt-2">
        <div>
          <CustomText
            text="Phone Number"
            className="inputLabel mb-2"
            fontWeight="font-semibold"
          />
          <div className="flex gap-2">
            <div
              className="flex items-center shrink-0 border border-roundedInputBorder rounded-md px-3 py-2 bg-sideNavGray text-royalBlue text-sm min-w-[4.5rem]"
              aria-label="Country code United States"
            >
              {US_DIAL_CODE}
            </div>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="000-000-0000"
              disabled={isBusy}
              className="border border-roundedInputBorder rounded-md px-3 py-2 w-full text-royalBlue text-sm placeholder:text-borderGray"
              value={nationalNumber}
              onChange={(e) => handlePhoneChange(e.target.value)}
            />
          </div>
          <p className="text-xs text-textSecondaryGray mt-3">
            By entering your phone number, you consent to receive SMS
            notifications from Castlebiosciences regarding order status
            updates, requests for additional information, and report
            availability. Msg & data rates may apply. Message frequency
            varies. Reply STOP to unsubscribe, HELP for help.
          </p>
        </div>

        <Button
          type="button"
          buttonTheme="secondary"
          className="w-full mt-4 flex items-center justify-center"
          disabled={isBusy || !isValidUsNationalNumber(nationalNumber)}
          loading={sendCodeMutation.isPending}
          onClick={handleReceiveOtp}
          textInformation={{
            text: sendCodeMutation.isPending ? "Sending…" : "Receive OTP",
            fontWeight: "font-semibold",
          }}
        />

        <div>
          <CustomText
            text="Verification code"
            className="inputLabel mb-2"
            fontWeight="font-semibold"
          />
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="Enter 6-digit code"
            disabled={!otpRequested || isBusy}
            className={`border border-roundedInputBorder rounded-md px-3 py-2 w-full text-sm placeholder:text-borderGray ${
              otpRequested
                ? "text-royalBlue bg-white"
                : "text-borderGray bg-sideNavGray cursor-not-allowed"
            }`}
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            buttonTheme="secondary"
            disabled={isBusy}
            onClick={onClose}
            textInformation={{
              text: "Cancel",
              fontWeight: "font-semibold",
            }}
          />
          <Button
            type="button"
            buttonTheme="primary"
            disabled={!otpRequested || isBusy || otp.length !== 6}
            loading={verifyCodeMutation.isPending || updating}
            onClick={() => {
              void handleVerifyOtp();
            }}
            textInformation={{
              text: "Verify OTP",
              fontWeight: "font-semibold",
            }}
          />
        </div>
      </div>
    </CustomPopUp>
  );
}

export default PhoneVerificationPopup;
